import {defineStore} from 'pinia';
import moment from 'moment';
import Configuration from '@core/services/ConfigProvider';
import snackbarService from '@core/services/SnackbarService';
import ApiService from '@core/services/ApiService';
import {AxiosRequestConfigLoader} from '@core/store/LoaderStore';
import {formatTimePassed} from '@core/utils/time';
import {formatNumber} from '@core/utils/number';
import {formatNumberLabel} from '@core/utils/chart-formatters';
import {
  filterMonacoPresetOverridesToActiveEditorThemes,
  mergeEditorThemeSettings,
  migrateEditorThemeStorageToCustom,
  type EditorThemeSettings,
} from '@core/utils/monacoEditorTheme';
import {
  mergeMonacoPresetThemeOverrides,
  type MonacoPresetThemeOverrides,
} from '@core/utils/mergeMonacoPresetTheme';
import {ref} from 'vue';
import {
  migrateLegacyStyleJson,
  type LegacyStylesPayload,
} from '@core/utils/migrateLegacyStyleJson';

const trustedReferrer = Configuration.value('trustedReferrer');

export interface StyleDetail {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
}

export interface StatusColors {
  success: string;
  warning: string;
  error: string;
  info: string;
}

export const DEFAULT_STATUS_COLORS: StatusColors = {
  success: '#10b981',
  warning: '#eab308',
  error: '#ef4444',
  info: '#3b82f6',
};

function mergeStatusColors(
  input?: Partial<StatusColors> | Record<string, string>,
): StatusColors {
  return {
    ...DEFAULT_STATUS_COLORS,
    ...input,
  };
}

export type SidebarSelectionMenuBase = 'primary' | 'secondary';

export interface Styles {
  dark: StyleDetail;
  light: StyleDetail;
  lightImage?: string;
  darkImage?: string;
  status?: StatusColors;
  /** Narrow icon sidebar: which brand color is used as its background (items use the other palette). */
  sidebarSelectionMenuBase?: SidebarSelectionMenuBase;
  /** Monaco editor theme id per app color mode (see MONACO_EDITOR_THEME_IDS). */
  editorTheme?: EditorThemeSettings;
  /** Optional overrides for built-in preset Monaco themes (see mergeMonacoPresetTheme.ts). */
  monacoPresetThemeOverrides?: MonacoPresetThemeOverrides;
}

/** Default `styles` shape for initial state and `$reset` (logout / reset all stores). */
export function createDefaultStyles(): Styles {
  return {
    dark: {
      primary: '#EF5717',
      secondary: '#EF5717',
      accent: '#EF5717',
      background: '#121212',
    },
    light: {
      primary: '#dbc7c0',
      secondary: '#706560',
      accent: '#EF5717',
      background: '#FAFAFA',
    },
    status: {...DEFAULT_STATUS_COLORS},
    sidebarSelectionMenuBase: 'secondary',
    editorTheme: mergeEditorThemeSettings(),
    monacoPresetThemeOverrides: mergeMonacoPresetThemeOverrides(),
  };
}

interface Language {
  id: string;
  flag: string;
  language: string;
  title: string;
  active: boolean;
}

export const useSettingsStore = defineStore(
  'SettingsStore',
  () => {
    const apiService = ApiService;

    const loading = ref<boolean>(false);
    const customCss = ref<string | undefined>();
    const logo = ref<string | undefined>();
    const darkModeEnabled = ref<boolean>(false);
    const swaggerUI = ref<boolean>(true);
    const rowsPerPage = ref<number>(10);
    const language = ref<string>('en-GB');
    const styles = ref<Styles>(createDefaultStyles());
    const images = ref<string[]>([]);
    const referrer = ref<string | null>(null);
    const languages = ref<Language[]>([]);

    function formatDate(date: string, options = {}): string {
      if (!date) {
        return '';
      }
      return new Date(date).toLocaleDateString(language.value, options);
    }

    async function getLanguages() {
      try {
        const response = await apiService.getApiInstance().get('/languages');
        languages.value = response.data;
      } catch {
        try {
          const response = await apiService
            .getDefaultApiInstance()
            .get('/languages');
          languages.value = response.data;
        } catch {
          languages.value = [];
        }
      }
    }

    async function setLanguageActive(languageId: string, isActive: boolean) {
      const languageRow = languages.value.find(l => l.id === languageId);
      const previousActive = languageRow?.active;
      if (languageRow) {
        languageRow.active = isActive;
      }

      try {
        const response = await apiService
          .getApiInstance()
          .patch(`/languages/${languageId}`, {active: isActive}, {
            showLoader: false,
          } as AxiosRequestConfigLoader);
        if (response.status === 204) {
          snackbarService.showHappySnackbar('Successfully updated settings');
        } else {
          if (languageRow) {
            languageRow.active = previousActive ?? !isActive;
          }
          snackbarService.showSadSnackbar(
            `Something went wrong. Status:  ${response.status}`,
          );
        }
      } catch {
        if (languageRow) {
          languageRow.active = previousActive ?? !isActive;
        }
        snackbarService.showSadSnackbar(
          'Could not update language. Please try again.',
        );
      }
    }

    async function getStyles() {
      const response = await apiService
        .getDefaultApiInstance()
        .get('/storage/settings/style.json');
      const data = migrateLegacyStyleJson(response.data as LegacyStylesPayload);
      const previousSidebar = styles.value.sidebarSelectionMenuBase;
      const editorTheme = mergeEditorThemeSettings(data.editorTheme);
      const monacoMerged = mergeMonacoPresetThemeOverrides(
        data.monacoPresetThemeOverrides,
      );
      const migrated = migrateEditorThemeStorageToCustom(
        editorTheme,
        monacoMerged,
      );
      styles.value = {
        ...data,
        status: mergeStatusColors(data.status),
        sidebarSelectionMenuBase:
          data.sidebarSelectionMenuBase ?? previousSidebar ?? 'secondary',
        editorTheme: migrated.editorTheme,
        monacoPresetThemeOverrides:
          filterMonacoPresetOverridesToActiveEditorThemes(
            mergeMonacoPresetThemeOverrides(migrated.overrides),
            migrated.editorTheme,
          ),
      };
    }

    async function setStyles(stylesPayload: Styles) {
      const editorTheme = mergeEditorThemeSettings(stylesPayload.editorTheme);
      const monacoMerged = mergeMonacoPresetThemeOverrides(
        stylesPayload.monacoPresetThemeOverrides,
      );
      const migrated = migrateEditorThemeStorageToCustom(
        editorTheme,
        monacoMerged,
      );
      const json = JSON.stringify({
        ...stylesPayload,
        status: mergeStatusColors(stylesPayload.status),
        sidebarSelectionMenuBase:
          stylesPayload.sidebarSelectionMenuBase ?? 'secondary',
        editorTheme: migrated.editorTheme,
        monacoPresetThemeOverrides:
          filterMonacoPresetOverridesToActiveEditorThemes(
            mergeMonacoPresetThemeOverrides(migrated.overrides),
            migrated.editorTheme,
          ),
      });
      const style = new Blob([json], {
        type: 'application/json',
      });
      const data = new FormData();
      data.append('document', style, 'style.json');
      const response = await apiService
        .getApiInstance()
        .post('/containers/settings/upload', data);
      if (response.status === 200) {
        snackbarService.showHappySnackbar('Successfully updated settings');
      } else {
        snackbarService.showSadSnackbar(
          `Something went wrong. Status:  ${response.status}`,
        );
      }
      await getStyles();
    }

    async function getImages() {
      const response = await apiService
        .getApiInstance()
        .get('/containers/images/files');
      images.value = response.data.map((image: {name: string}) => image.name);
    }

    async function setImage(input: {
      type: 'darkImage' | 'lightImage';
      imageName: string;
    }) {
      styles.value[input.type] = input.imageName;
      const json = JSON.stringify(styles.value);
      const style = new Blob([json], {
        type: 'application/json',
      });
      const data = new FormData();
      data.append('document', style, 'style.json');
      await apiService
        .getApiInstance()
        .post('/containers/settings/upload', data);
      await getStyles();
    }

    async function uploadImage(image: File) {
      const data = new FormData();
      data.append('file', image, image.name);
      const response = await apiService
        .getApiInstance()
        .post('/containers/images/upload', data, {
          headers: {'Content-Type': 'multipart/form-data'},
        });
      if (response.status === 200) {
        snackbarService.showHappySnackbar(
          `Successfully uploaded ${image.name}`,
        );
      } else {
        snackbarService.showSadSnackbar(
          `Something went wrong. Status:  ${response.status}`,
        );
      }
      await getImages();
    }

    async function deleteImage(index: number) {
      const response = await apiService
        .getApiInstance()
        .delete(`/containers/images/files/${images.value[index]}`);
      if (response.status === 204) {
        snackbarService.showHappySnackbar('Successfully deleted image');
      } else {
        snackbarService.showSadSnackbar(
          `Something went wrong. Status:  ${response.status}`,
        );
      }
      await getImages();
    }

    function changeUI() {
      swaggerUI.value = !swaggerUI.value;
    }

    function darkMode() {
      darkModeEnabled.value = !darkModeEnabled.value;
    }

    function setLanguageState(localeCode: string) {
      language.value = localeCode;
      if (localeCode === 'nl-NL') {
        moment.updateLocale('nl', {
          relativeTime: {
            future: 'in %s',
            past: '%s geleden',
            s: 'sec',
            ss: '%s sec',
            m: '1 min',
            mm: '%d min',
            h: '1 uur',
            hh: '%d u',
            d: '1 dag',
            dd: '%d d',
            M: '1 maand',
            MM: '%d M',
            y: '1 jaar',
            yy: '%d jaren',
          },
        });
      } else {
        moment.updateLocale('en', {
          relativeTime: {
            future: 'in %s',
            past: '%s ago',
            s: 'sec',
            ss: '%s sec',
            m: '1 min',
            mm: '%d min',
            h: '1 hour',
            hh: '%d h',
            d: '1 day',
            dd: '%d d',
            M: '1 month',
            MM: '%d M',
            y: 'a year',
            yy: '%d Y',
          },
        });
      }
    }

    function SET_REFERRER(referrerItem: string) {
      if (trustedReferrer && referrerItem.startsWith(trustedReferrer)) {
        referrer.value = referrerItem;
      }
    }

    function EMPTY_REFERRER() {
      referrer.value = null;
    }

    function $reset() {
      loading.value = false;
      darkModeEnabled.value = false;
      swaggerUI.value = true;
      rowsPerPage.value = 10;
      language.value = 'en-GB';
      styles.value = createDefaultStyles();
      images.value = [];
      referrer.value = null;
      languages.value = [];
    }

    function setCustomCss(css: string) {
      customCss.value = css;
      let styleTag = document.querySelector('#custom-css');
      if (styleTag) {
        styleTag.remove();
      }
      if (css) {
        styleTag = document.createElement('style');
        styleTag.id = 'custom-css';
        styleTag.textContent = css;
        document.head.append(styleTag);
      }
    }

    function setLogo(logoUrl: string) {
      logo.value = logoUrl;
    }

    function removeLogo() {
      logo.value = undefined;
    }

    return {
      loading,
      darkModeEnabled,
      swaggerUI,
      rowsPerPage,
      language,
      styles,
      images,
      referrer,
      languages,
      formatTimePassed,
      formatDate,
      formatNumber,
      formatNumberLabel,
      getLanguages,
      setLanguageActive,
      getStyles,
      setStyles,
      getImages,
      setImage,
      uploadImage,
      deleteImage,
      changeUI,
      darkMode,
      setLanguageState,
      customCss,
      setCustomCss,
      logo,
      setLogo,
      removeLogo,
      SET_REFERRER,
      EMPTY_REFERRER,
      $reset,
    };
  },
  {persist: true},
);
